import React, { createContext, useContext, useEffect, useState } from "react";
import { getUser } from "../utils/auth/AuthenticatedUser";
import { getErrorType, getNameFromUpn, formatDate } from "../utils/common";
import {
  isSignedInUserATeamOwner,
  isUserAdmin,
  buildSendersList,
} from "../utils/user";
import { getUserTeams, getAllTeams, getTeamUsers } from "../APIs/UserAPI";
import { getEnabledToggles } from "../APIs/ToggleAPI";
import { getCcmConfig } from "../APIs/CcmAPI";
import { TEAM } from "../consts/DBFields";
import { JWT } from "../consts/AuthFields";
import {
  DEFAULT_PAGE,
  ERROR_TYPES,
  FEATURE,
  LOCAL_STORAGE,
  SNACK_DURATION,
} from "../consts/Common";
import Configs from "../Configurations";
import _ from 'lodash';

// Create and export the consumer, which allows state to be used by other components
export const Context = createContext();
export const AppState = Context.Consumer;
export const useAppContext = () => useContext(Context);

// Create and export the provider, which defines and controls the state
function Core({ children }) {
  const [activePage, setActivePage] = useState(DEFAULT_PAGE);
  const [user, setUser] = useState({});
  const [signedInUserIsOwner, toggleSignedInUserIsOwner] = useState(false);
  const [sendersList, setSendersList] = useState([]);
  const [userTeams, setTeams] = useState([]);
  const [loadingFeatures, toggleLoadingFeatures] = useState(false);
  const [toggles, setToggles] = useState([]);
  const [loadingTeamUsers, toggleLoadingTeamUsers] = useState(true);
  const [teamUsers, setTeamUsers] = useState({});
  const [selectedTeam, setSelectedTeam] = useState({});
  const [authError, setAuthError] = useState(false);
  const [appError, setAppError] = useState(false);
  const [snackMessage, setSnackMessage] = useState(null);
  const [snackType, setSnackType] = useState();
  const [snackPosition, setSnackPosition] = useState({
    vertical: "bottom",
    horizontal: "center",
  });
  const [overriddenSnackCss, setOverriddenSnackCss] = useState(null);
  const [snackDuration, setSnackDuration] = useState(SNACK_DURATION);
  const [ccmConfig, setCcmConfig] = useState({});
  const [folder_id, setFolderId] = useState(null);
  const [pageTitle, setPageTitle] = useState("")

  //New V2 UI Enabled flag
  const [isV2_UI, setIsV2_UI] = useState(null);

  const [authResolved, setAuthResolved] = useState(false);

  useEffect(() => {
    if (!user || !user[JWT.UPN]) {
      try {
        setUser(getUser());
      } catch (err) {
        //  SSO will handle this error
      }
    }
    setAuthResolved(true);
  }, []);

  useEffect(() => {
    if (!authResolved) return;
    setUserTeams(user);
    setFeatureToggles(user);
  }, [user[JWT.UPN], authResolved]);

  useEffect(() => {
    async function handleTeamChange() {
      await getMembersOfTeam(selectedTeam?.[TEAM.ID]);
    }
    handleTeamChange();
  }, [selectedTeam[TEAM.ID], activePage]);

  useEffect(() => {
    if (
      isUserAdmin(user) ||
      isSignedInUserATeamOwner(user, teamUsers?.[selectedTeam?.[TEAM.ID]])
    ) {
      toggleSignedInUserIsOwner(true);
    } else {
      toggleSignedInUserIsOwner(false);
    }
  }, [user, selectedTeam?.[TEAM.ID], teamUsers?.[selectedTeam?.[TEAM.ID]]]);

  useEffect(() => {
    const updateSendersList = async () => {
      const senders = await buildSendersList(
        selectedTeam,
        signedInUserIsOwner,
        teamUsers,
        user
      );
      setSendersList(senders);
    };
    updateSendersList();
  }, [selectedTeam, signedInUserIsOwner, teamUsers?.[selectedTeam?.[TEAM.ID]]]);

  useEffect(() => {
    const isV2_UIToggleEnabled = isToggleEnabled(FEATURE.UI_V2);
    setIsV2_UI(() => isV2_UIToggleEnabled);
    const getConfigFromCcm = async () => {
      const isAlertBannerEnabled = isToggleEnabled(FEATURE.SHOW_ALERT_BANNER);
      const ccmConfig = isAlertBannerEnabled && toggles ? await getCcmConfig() : [];
      setCcmConfig(ccmConfig);
    };
    getConfigFromCcm();
  }, [toggles]);

  const isToggleEnabled = (toggle) => {
    return toggles?.includes(toggle);
  };

  const setFeatureToggles = async (user) => {
    let toggles = [];
    if (user && user[JWT.UPN]) {
      toggleLoadingFeatures(true);
      try {
        toggles = await getEnabledToggles(user[JWT.UPN]);
        if (toggles && toggles.length) {
          setToggles(toggles);
        } else {
          setToggles([]);
        }
      } catch (err) {
        setToggles([]);
      } finally {
        toggleLoadingFeatures(false);
      }
    } else {
      setToggles([]);
    }
  };

  const redirectToOnboarding = () => {
    const baseUrl = Configs.REACT_APP_SPARK_COMMS_URL;
    if (!baseUrl) {
      console.error(
        "[Core] REACT_APP_SPARK_COMMS_URL is not configured; cannot redirect to onboarding."
      );
      return;
    }
    window.location.href = `${baseUrl}/teams`;
  };

  const setUserTeams = async (user) => {
    let teams = [];
    if (user && user[JWT.UPN]) {
      try {
        // Always check if user is part of any team, regardless of role
        // For admins, show all teams; for non-admins, show only their teams
        if(isUserAdmin(user)) {
          teams = await getAllTeams();
        } else {
          teams = await getUserTeams(user[JWT.UPN]);
        }
        const userTeamList = teams;
        if (userTeamList && userTeamList.length) {
          // Priority: team last selected in spark-comms → team last selected in
          // sn-ui → first team in list. This ensures navigating from spark-comms
          // to sn-ui (full page load) lands on the same team the user had open.
          const sparkCommsTeamId = localStorage.getItem("spark_selected_team_id");
          const snUiRecentTeamId = localStorage.getItem(LOCAL_STORAGE.RECENT_TEAM_ID);
          const recentTeam =
            userTeamList.find((team) => team[TEAM.ID] === sparkCommsTeamId) ||
            userTeamList.find((team) => team[TEAM.ID] === snUiRecentTeamId);
          setSelectedTeam(recentTeam || userTeamList[0]);
        } else {
          // User is authenticated but has no teams — redirect to onboarding
          redirectToOnboarding();
        }
      } catch (err) {
        const status = err?.response?.status || err?.status;
        console.log("[Core] Error fetching user teams:", status);
        if (status === 401 || status === 403) {
          // User is not authorized or not permitted — redirect to onboarding
          redirectToOnboarding();
        } else {
          setAppError(getErrorType(err));
        }
      }
    } else {
      redirectToOnboarding();
    }
    setTeams(teams);
  };

  const getMembersOfTeam = async (teamId) => {
    if (teamId && !teamUsers?.[teamId]) {
      toggleLoadingTeamUsers(true);
      try {
        let curentTeamUsers = await getTeamUsers(teamId);
        if (!curentTeamUsers) curentTeamUsers = [];
        //put owners and co-owners at the top of the list
        const sortedTeamUsers = curentTeamUsers.sort((a, b) => a.role - b.role);
        setTeamUsers({
          ...teamUsers,
          [teamId]: sortedTeamUsers,
        });
        toggleLoadingTeamUsers(false);
        return sortedTeamUsers;
      } catch (err) {
        toggleLoadingTeamUsers(false);
        // TODO handle error
      }
    }
    return;
  };

  const isSystemNotificationsEnabled = () => {
    if(!_.isEmpty(selectedTeam) && selectedTeam[TEAM.IS_SYSTEM_NOTIFICATIONS_ENABLED] === true) {
      return true;
    }
    return false;
  }

  // create one state object to pass to the Provider
  const initialState = {
    activePage,
    setActivePage,
    user,
    setUser: (user) => setUser(user ? user : {}),
    userTeams,
    setUserTeams,
    loadingTeamUsers,
    toggles,
    isToggleEnabled,
    teamUsers,
    setTeamUsers,
    selectedTeam,
    setSelectedTeam: (team) => {
      localStorage.setItem(LOCAL_STORAGE.RECENT_TEAM_ID, team?.[TEAM.ID]);
      setSelectedTeam(team ? team : {});
    },
    authError,
    appError,
    setAppError,
    setAuthError,
    snackMessage,
    clearSnackMessage: () => {
      setSnackMessage(null);
      setOverriddenSnackCss(null);
    },
    setSnackMessage,
    setSnackType,
    snackPosition,
    setSnackPosition,
    snackDuration,
    setSnackDuration,
    overriddenSnackCss,
    setOverriddenSnackCss,
    snackType,
    sendersList,
    signedInUserIsOwner,
    getMembersOfTeam,
    loadingFeatures,
    getNameFromUpn: (upn, abbreviateUpn = true) => {
      return getNameFromUpn(upn, selectedTeam, teamUsers, abbreviateUpn);
    },
    ccmConfig,
    folder_id,
    setFolderId,
    pageTitle,
    setPageTitle,
    isSystemNotificationsEnabled,
    isV2_UI,
  };
  return <Context.Provider value={initialState}>{children}</Context.Provider>;
}

export default Core;
